<template>
    <div class="choose-plan">
        <PurchaseBlob v-if="!validReferral" class="choose-plan__blob" />
        <div class="choose-plan__info">
            Study on the go, when and how you want.
        </div>
        <Plans
            :exam="exam"
            class="choose-plan__plans"
            :bundle="bundle"
            :saved-selected-plan-id="savedSelectedPlanId"
            :valid-referral="validReferral"
            :referral-error="referralError"
            :referral-metadata-preselected-plan="referralMetadataPreselectedPlan"
            @confirmPlan="handleConfirmPlan"
        >
            <template #secondaryAction>
                <slot name="secondaryAction"></slot>
            </template>
        </Plans>
        <ConfidenceLevel
            class="choose-plan__confidence-level"
            :show-compare-view="showCompareView"
            :exam="exam"
            @toggleCompareView="toggleCompareView"
        />
        <Compare
            class="choose-plan__compare"
            :bundle="bundle"
            :exam="exam"
            :show-compare-view="showCompareView"
            :exam-has-level-up-mode="examHasLevelUpMode"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import Plans from '@/components/Register/ChoosePlan/Plans.vue'
import ConfidenceLevel from '@/components/Register/ChoosePlan/ConfidenceLevel.vue'
import Compare from '@/components/Register/ChoosePlan/Compare.vue'
import PurchaseBlob from '@/assets/onboarding/PurchaseBlob.vue'
import { analyticsModule } from '@/store/analytics/module'
import { userModule } from '@/store/user/module'

withDefaults(
    defineProps<{
        exam: Study.Class.ExamMetadataJSON | ClientExam
        bundle: Study.Class.BundleJSON | ClientBundle
        savedSelectedPlanId: string | null
        validReferral?: boolean
        referralError?: string | null
        examHasLevelUpMode?: boolean
        referralMetadataPreselectedPlan: string | null
    }>(),
    {
        validReferral: false,
        referralError: null,
        examHasLevelUpMode: false,
    }
)

const emit = defineEmits<{
    confirmPlan: [planId: string | null]
}>()

const showCompareView = ref(false)

const hasExpandedCompareTable = computed(() => {
    return userModule.state.user?.webConfig?.hasViewedCompareTable
})

onMounted(async () => {
    await userModule.actions.fetchUserData()
})

const toggleCompareView = async () => {
    if (!hasExpandedCompareTable.value) {
        // check if the user has seen the compare before
        await userModule.actions.updateWebConfig({
            hasViewedCompareTable: true,
        })

        await analyticsModule.actions.trackEvent('Choose_Plan_Table_Expanded')
    }

    showCompareView.value = !showCompareView.value
    if (showCompareView.value) {
        setTimeout(() => {
            const compareEl = document.querySelector('.choose-plan__compare')
            compareEl?.scrollIntoView({ behavior: 'smooth' })
        }, 200)
    }

    if (!showCompareView.value) {
        const blobEl = document.querySelector('.choose-plan__blob')
        blobEl?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
}

const handleConfirmPlan = (planId: string | null) => {
    emitConfirmPlan(planId)
}

const emitConfirmPlan = (planId: string | null) => {
    emit('confirmPlan', planId)
}
</script>
<style lang="scss" scoped>
.choose-plan {
    width: 100%;

    &__blob {
        position: absolute;
        top: 0;
        right: 0;
        z-index: -2;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__info {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 44px;
        margin-top: 4px;

        @include breakpoint(black-bear) {
            margin-bottom: 24px;
        }
    }

    &__compare {
        color: $brand-blue;
        font-size: 15px;
        line-height: 20px;
        text-align: center;
        font-weight: 600;
        position: relative;
        z-index: 1;

        @include breakpoint (brown-bear) {
           margin: 12px auto 32px;
        }
    }

    &__compare-panel {
        @include breakpoint (brown-bear) {
            margin-bottom: 34px;
        }
    }

    &__compare-arrow {
        width: 9px;
        margin-left: 2px;
        display: none;

        @include breakpoint (brown-bear) {
            display: inline-block;
        }

        &--open {
            transform: rotate(180deg);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 50px;
    }

    &__try-free-btn {
        margin-right: 8px;
    }

    &__purchase-btn-arrow {
        width: 12px;
        margin-top: 4px;
        margin-left: 14px;
    }
}
</style>
